import { useMemo, useCallback, useEffect, useRef, useState } from "react";
import { Grid, GridColumn as Column } from "@progress/kendo-react-grid";
import { Tooltip } from "@fluentui/react-components";
import {
  ProjectIdCell,
  LevfinBankerEmptyState,
  LevfinBankerNoDealOnFilterState,
  LevfinBankerNoDealOnSearchState,
  renderCellContent,
  getAccordionRowRender,
  applySearchFilter,
  getFilteredSortedData,
  useGroupToggle2,
  LevfinCapacity,
  getFlattenedData,
  LevfinNoDealonDealsTrackerEmptyState,
} from "../levfin-helper";
import {
  renderTooltipHeader,
  createColumnMenu,
  highlightMatch,
} from "../../dashboard-util/dashboard-telerik-helper/dashboard-telerik-helper";
import { isFilteredColumn } from "../../dashboard-util/dashboard-telerik-helper/widget-grid-filter";
import PropTypes from "prop-types";
import "../levfin-junior-banker/levfin-time-log.scss";
import DealTeamAvatarGroupCell from "../../dashboard-util/dashboard-telerik-helper/deal-team-avatar";
import { FluentAvatar } from "../../../../components/FluentUI/Fluent-Avatar";
import LevfinDealTrackerModal from "./levfin-deal-tracker-modal";
import { useHistory } from "react-router-dom";

const LevfinDealTrackerDetail = ({
  data = [],
  canCreate,
  detailState,
  onDetailStateChange,
  searchValue,
  resetDataState,
  refineSearch,
  categoryOptions,
  emptyCategories,
  updateSummaryNumbers,
  isStafferView
}) => {
  const hasMounted = useRef(false);
  const history = useHistory();
  const { sort, filter } = detailState;
  const { groupState, toggleGroup, setGroupState } = useGroupToggle2();
  const flattenedData = useMemo(() => getFlattenedData(data), [data]);
  const filteredData = useMemo(() => getFilteredSortedData(flattenedData, filter, sort, categoryOptions), [flattenedData, filter, sort, categoryOptions]);
  const visibleData = useMemo(() => filteredData, [filteredData]);
  const [showModal, setShowModal] = useState(false);
  const [selectedDeal, setSelectedDeal] = useState(null);

  useEffect(() => {
    if (hasMounted.current) {
      applySearchFilter(searchValue, detailState, onDetailStateChange, setGroupState);
    } else {
      hasMounted.current = true;
    }
  }, [searchValue]);

  useEffect(() => {
    updateSummaryNumbers(visibleData);
  }, [visibleData]);

  const columnMenu = useMemo(() => createColumnMenu(flattenedData, searchValue), [flattenedData, searchValue]);
  const columnIsFiltered = useCallback(
    (fieldName) => isFilteredColumn(fieldName, filter),
    [filter]
  );
  const AccordionRowRender = useMemo(() => getAccordionRowRender(filteredData, groupState, toggleGroup, 10, filter, categoryOptions, emptyCategories), [filteredData, groupState, filter, categoryOptions, emptyCategories]);
  const isEmpty = visibleData.length === 0;

  const finalGridData = isEmpty
    ? [{
      isEmptyRow: true,
      isSearchEmptyState: searchValue?.trim() !== "",
    }]
    : visibleData;

  const onHide = () => {
    setShowModal(false);
    setSelectedDeal(null);
  };

  const handleCellClick = (dataItem) => (e) => {
    if (dataItem.isEmptyRow) return;
    setSelectedDeal(dataItem);
    setShowModal(true);
  };

  const handleDealTeamCellClick = (dataItem) => (e) => {
    if (dataItem.isEmptyRow) return;
    if (isStafferView && !dataItem.DealTeam?.length && dataItem.CanEdit) {
      history.push({
        pathname: `/projects/edit/${dataItem.DealId}`,
        state: { fromDashboard: true },
      });
      return;
    }
    else {
      setSelectedDeal(dataItem);
      setShowModal(true);
    }
  };
  return (
    <div className="detail-grid-scroll-container clickable-row levfin-deal-tracker-detail">
      <Grid
        data={finalGridData}
        rowRender={AccordionRowRender}
        sortable={true}
        sort={detailState.sort}
        filter={detailState.filter}
        onSortChange={(e) => onDetailStateChange({ ...detailState, sort: e.sort })}
        onFilterChange={(e) =>
          onDetailStateChange({ ...detailState, filter: e.filter })
        }
        navigable={true}
        keyboardNavigation={true}
        dataItemKey="ProjectId"
        resizable={true}
      // onRowClick={onRowClick}
      >
        <Column
          field="DealId"
          title={renderTooltipHeader("#")}
          filter="text"
          columnMenu={columnMenu}
          headerClassName={columnIsFiltered("DealId") ? "filtered" : ""}
          cell={(props) =>
            props.dataItem.isEmptyRow ? (
              <td colSpan={10} >
                {props.dataItem.isSearchEmptyState ? (
                  <LevfinBankerNoDealOnSearchState
                    resetDataState={resetDataState}
                    refineSearch={refineSearch}
                  />
                )  : (
                  <LevfinNoDealonDealsTrackerEmptyState canCreate={canCreate} />
                )}
              </td>
            ) : (
              <td className="background-white k-table-td">
                <ProjectIdCell
                  {...props}
                  canEdit={props.dataItem.CanEdit}
                  categoryOptions={categoryOptions}
                  isVisible={isStafferView || props.dataItem.CanEdit}
                />
              </td>
            )
          }
        />
        <Column
          field="DealName"
          title={renderTooltipHeader("Deal Name")}
          filter="text"
          columnMenu={columnMenu}
          headerClassName={columnIsFiltered("DealName") ? "filtered" : ""}
          cell={(props) =>
            renderCellContent(visibleData.length, null, (
              <td className="background-white k-table-td" onClick={handleCellClick(props.dataItem)}>
                <Tooltip content={props.dataItem.DealName} relationship="label">
                  <span>{highlightMatch(props.dataItem.DealName, searchValue)}</span>
                </Tooltip>
              </td>
            ))
          }
        />
        <Column
          field="ProcessType"
          title={renderTooltipHeader("Process Type")}
          filter="text"
          columnMenu={columnMenu}
          headerClassName={columnIsFiltered("ProcessType") ? "filtered" : ""}
          cell={(props) =>
            renderCellContent(visibleData.length, null, (
              <td className="background-white k-table-td" onClick={handleCellClick(props.dataItem)}>
                <Tooltip content={props.dataItem.ProcessType} relationship="label">
                  <span>
                    {highlightMatch(props.dataItem.ProcessType, searchValue)}
                  </span>
                </Tooltip>
              </td>
            ))
          }
        />
        <Column
          field="Industry"
          title={renderTooltipHeader("Industry")}
          filter="text"
          columnMenu={columnMenu}
          headerClassName={columnIsFiltered("Industry") ? "filtered" : ""}
          cell={(props) =>
            renderCellContent(visibleData.length, null, (
              <td className="background-white k-table-td" onClick={handleCellClick(props.dataItem)}>
                <Tooltip content={props.dataItem.Industry} relationship="label">
                  <span>{highlightMatch(props.dataItem.Industry, searchValue)}</span>
                </Tooltip>
              </td>
            ))
          }
        />
        <Column
          field="Deliverable"
          title={renderTooltipHeader("Deliverable")}
          filter="text"
          columnMenu={columnMenu}
          headerClassName={columnIsFiltered("Deliverable") ? "filtered" : ""}
          cell={(props) =>
            renderCellContent(visibleData.length, null, (
              <td className="background-white k-table-td" onClick={handleCellClick(props.dataItem)}>
                <Tooltip content={props.dataItem.Deliverable} relationship="label">
                  <span>{highlightMatch(props.dataItem.Deliverable, searchValue)}</span>
                </Tooltip>
              </td>
            ))
          }
        />
        <Column
          field="TimeCommitment"
          title={renderTooltipHeader("Deal Time Commitment")}
          filter="text"
          columnMenu={columnMenu}
          headerClassName={columnIsFiltered("TimeCommitment") ? "filtered" : ""}
          cell={(props) =>
            renderCellContent(visibleData.length, null, (
              <td className="background-white k-table-td" onClick={handleCellClick(props.dataItem)}>
                <LevfinCapacity capacity={props.dataItem.TimeCommitment} />
              </td>
            ))
          }
        />
        <Column
          field="MD_D"
          title={renderTooltipHeader("MD / D")}
          filter="text"
          columnMenu={columnMenu}
          headerClassName={columnIsFiltered("MD_D") ? "filtered" : ""}
          cell={(props) =>
            renderCellContent(visibleData.length, null, (
              <td className="background-white k-table-td avatar-cell" onClick={handleCellClick(props.dataItem)}>
                <Tooltip content={props.dataItem.MD_D} relationship="label">
                  <div className="avatar-content">
                    <FluentAvatar name={props.dataItem.MD_D} color="cornflower" size={24} />
                    <span>{highlightMatch(props.dataItem.MD_D, searchValue)}</span>
                  </div>
                </Tooltip>
              </td>
            ))
          }
        />
        <Column
          field="D_VP"
          title={renderTooltipHeader("D / VP")}
          filter="text"
          columnMenu={columnMenu}
          headerClassName={columnIsFiltered("D_VP") ? "filtered" : ""}
          cell={(props) =>
            renderCellContent(visibleData.length, null, (
              <td className="background-white k-table-td avatar-cell" onClick={handleCellClick(props.dataItem)}>
                <Tooltip content={props.dataItem.D_VP} relationship="label">
                  <div className="avatar-content">
                    <FluentAvatar name={props.dataItem.D_VP} color="pink" size={24} />
                    <span>{highlightMatch(props.dataItem.D_VP, searchValue)}</span>
                  </div>
                </Tooltip>
              </td>
            ))
          }
        />
        <Column
          field="Staffers"
          title={renderTooltipHeader("Staffer(s)")}
          filter="text"
          columnMenu={columnMenu}
          headerClassName={columnIsFiltered("Staffers") ? "filtered" : ""}
          cell={(props) =>
            renderCellContent(visibleData.length, null, (
              <td className="background-white k-table-td" onClick={handleCellClick(props.dataItem)}>
                {props.dataItem.Staffers?.length
                  ? <DealTeamAvatarGroupCell members={props.dataItem.Staffers} />
                  : "—"
                }
              </td>
            ))
          }
        />
        <Column
          field="DealTeam"
          title={renderTooltipHeader("Deal Team")}
          filter="text"
          columnMenu={columnMenu}
          headerClassName={columnIsFiltered("DealTeam") ? "filtered" : ""}
          cell={(props) =>
            renderCellContent(visibleData.length, null, (
              <td className="background-white k-table-td" onClick={handleDealTeamCellClick(props.dataItem)}>
                {props.dataItem.DealTeam?.length
                  ? <DealTeamAvatarGroupCell members={props.dataItem.DealTeam} />
                  : (props.dataItem.CanEdit && isStafferView) ? <span className='lefvinAssignMembersText'>Assign Members</span> : "—"
                }
              </td>
            ))
          }
        />
      </Grid>
      {showModal && (
        <LevfinDealTrackerModal
          show={showModal}
          onHide={onHide}
          deal={selectedDeal}
          categoryOptions={categoryOptions}
        />
      )}
    </div>
  );
};

LevfinDealTrackerDetail.propTypes = {
  data: PropTypes.arrayOf(PropTypes.object).isRequired,
  canCreate: PropTypes.bool.isRequired,
  detailState: PropTypes.shape({
    sort: PropTypes.arrayOf(
      PropTypes.shape({
        field: PropTypes.string,
        dir: PropTypes.oneOf(["asc", "desc"]),
      })
    ),
    filter: PropTypes.object,
  }).isRequired,
  onDetailStateChange: PropTypes.func.isRequired,
  searchValue: PropTypes.string,
  resetDataState: PropTypes.func.isRequired,
  refineSearch: PropTypes.func.isRequired,
  categoryOptions: PropTypes.arrayOf(PropTypes.string),
  emptyCategories: PropTypes.array,
  updateSummaryNumbers: PropTypes.func.isRequired,
};

export default LevfinDealTrackerDetail;